import React, { useContext, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BusinessAutomationActionTypeEnum } from '@reach/shared-types/business-automation';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { useToast } from '../../../../hooks/useToast';
import { StepDialogFooter } from '../StepDialogFooter';
import styles from './WaitDialog.module.css';

type WaitAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: BusinessAutomationActionTypeEnum.WAIT_FOR_DURATION }
>;

type TimeUnit = 'minutes' | 'hours' | 'days' | 'weeks';

interface WaitDialogProps {
  open: boolean;
  onClose: () => void;
  action: WaitAction;
  actionIndex: number;
}

/**
 * Converts a duration value and unit to days, hours, minutes, seconds
 * If weeks is selected, stores as days * 7 and zeros out other fields
 */
function convertToDuration(
  value: number,
  unit: TimeUnit,
): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} {
  const result = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (unit === 'weeks') {
    result.days = value * 7;
  } else {
    result[unit] = value;
  }

  return result;
}

function convertFromDuration(duration: {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}): { value: number; unit: TimeUnit } {
  const { days = 0, hours = 0, minutes = 0 } = duration;

  let value: number;
  let unit: TimeUnit;

  // If all are zero, default to 1 minute
  if (!days && !hours && !minutes) {
    value = 1;
    unit = 'minutes';
  } else if (days > 0 && days % 7 === 0) {
    value = days / 7;
    unit = 'weeks';
  } else if (days > 0) {
    value = days;
    unit = 'days';
  } else if (hours > 0) {
    value = hours;
    unit = 'hours';
  } else if (minutes > 0) {
    value = minutes;
    unit = 'minutes';
  } else {
    value = 1;
    unit = 'minutes';
  }

  return { value, unit };
}

export const WaitDialog: React.FC<WaitDialogProps> = ({ open, onClose, action, actionIndex }) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateAction } = editor;
  const { addToast } = useToast();

  const { value, unit } = convertFromDuration({
    days: action.days,
    hours: action.hours,
    minutes: action.minutes,
    seconds: action.seconds,
  });

  const [durationValue, setDurationValue] = useState<number | ''>(value);
  const [timeUnit, setTimeUnit] = useState<TimeUnit>(unit);

  // Reset form data when dialog opens or action changes
  useEffect(() => {
    if (open) {
      const formData = convertFromDuration({
        days: action.days,
        hours: action.hours,
        minutes: action.minutes,
        seconds: action.seconds,
      });
      setDurationValue(formData.value);
      setTimeUnit(formData.unit);
    }
  }, [open, action]);

  if (!open) return null;

  const handleOK = () => {
    if (durationValue === '') {
      addToast({
        variant: 'destructive',
        title: 'Duration is required',
        description: 'Please enter a valid duration value.',
      });
      return;
    }

    const duration = convertToDuration(durationValue, timeUnit);
    updateAction(actionIndex, {
      days: duration.days,
      hours: duration.hours,
      minutes: duration.minutes,
      seconds: duration.seconds,
    });
    onClose();
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const value = parseInt(inputValue, 10);
    if (!isNaN(value) && value >= 0) {
      setDurationValue(value);
    } else {
      addToast({
        variant: 'destructive',
        title: 'Invalid duration',
        description: 'Please enter a valid duration value.',
      });
    }
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <h2>Configure Wait</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <X />
          </button>
        </header>

        <main>
          <form>
            <label htmlFor="duration-value">Duration</label>
            <div className={styles.durationInputs}>
              <input
                id="duration-value"
                type="number"
                min="0"
                value={durationValue}
                onChange={handleValueChange}
                placeholder="Enter duration"
                className={styles.numberInput}
              />
              <select
                id="duration-unit"
                value={timeUnit}
                onChange={e => setTimeUnit(e.target.value as TimeUnit)}
                className={styles.selectInput}>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
              </select>
            </div>
            <p className={styles.helpText}>
              The automation will wait for the specified duration before proceeding to the next step.
            </p>
          </form>
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>
    </>
  );
};
